import { useState } from 'react';
import { IoChevronDown, IoCheckmark } from 'react-icons/io5';

import { type IGamificationInfo } from '@/templates/WebCheckoutMercadoPago/@interfaces';

import styles from './GamificationCard.module.scss';

const STEPS_COUNT = 6;
const URGENT_THRESHOLD_DAYS = 5;
const TOTAL_DISCOUNT = 300;

const CRESCENTE_VALUES = [0, 20, 40, 60, 80, 100];
const DECRESCENTE_VALUES = [0, 100, 80, 60, 40, 20];

interface IGamificationCardProps {
  gamificationData: IGamificationInfo;
}

const pad = (n: number) => String(n).padStart(2, '0');

const formatCycleDate = (date: Date) => {
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

const formatStepValue = (value: number) =>
  value === 0 ? 'R$ 0' : `Até R$ ${value}`;

export const GamificationCard = ({
  gamificationData,
}: IGamificationCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const {
    seq_atual,
    grupo,
    cycle_starts_at,
    cycle_ends_at,
    cycle_ended,
  } = gamificationData;

  const values =
    grupo === 'decrescente' ? DECRESCENTE_VALUES : CRESCENTE_VALUES;

  const hasCycleDates = !!cycle_starts_at && !!cycle_ends_at;
  const cycleStartDate = hasCycleDates ? new Date(cycle_starts_at) : null;
  const cycleEndDate = hasCycleDates ? new Date(cycle_ends_at) : null;

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysRemaining = cycleEndDate
    ? Math.max(0, Math.ceil((cycleEndDate.getTime() - Date.now()) / msPerDay))
    : 0;

  const isUrgent = !cycle_ended && hasCycleDates && daysRemaining <= URGENT_THRESHOLD_DAYS;

  const progressPct = (() => {
    if (cycle_ended) return 100;
    if (!cycleStartDate || !cycleEndDate) return 0;

    const total = cycleEndDate.getTime() - cycleStartDate.getTime();
    if (total <= 0) return 0;

    const elapsed = Date.now() - cycleStartDate.getTime();

    return Math.min(Math.max((elapsed / total) * 100, 0), 100);
  })();

  const activeStep = Math.min(seq_atual + 1, STEPS_COUNT);
  const showCycleInfo = (hasCycleDates && seq_atual > 0) || cycle_ended;

  const title = cycle_ended
    ? 'Parabéns, você aproveitou todos os descontos deste ciclo!'
    : 'Acumule descontos a cada pacote!';

  const subtitle = (() => {
    if (cycle_ended) {
      return 'No próximo ciclo, você pode aproveitá-los novamente, fique atento.';
    }

    if (seq_atual === 0) {
      return `Você pode ganhar até R$ ${TOTAL_DISCOUNT} de desconto.`;
    }

    const currentValue = values[Math.min(seq_atual, values.length - 1)];

    return `Você tem até R$ ${currentValue} de desconto no seu próximo pacote.`;
  })();

  return (
    <div
      className={`${styles.card} ${cycle_ended ? styles['card--ended'] : ''}`}
    >
      <div className={styles.body}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.subtitle}>{subtitle}</p>

        <div className={styles.steps}>
          {Array.from({ length: STEPS_COUNT }).map((_, index) => {
            const stepNum = index + 1;
            const isCompleted = cycle_ended || stepNum < activeStep;
            const isActive = !cycle_ended && stepNum === activeStep;

            const circleClass = [
              styles.circle,
              isActive ? styles['circle--active'] : '',
              isCompleted ? styles['circle--completed'] : '',
            ]
              .filter(Boolean)
              .join(' ');

            const labelClass = [
              styles.label,
              isActive ? styles['label--active'] : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div key={stepNum} className={styles.step}>
                <div className={circleClass}>
                  {isCompleted ? (
                    <IoCheckmark size={18} />
                  ) : (
                    <span>{stepNum}º</span>
                  )}
                </div>
                <span className={labelClass}>
                  {formatStepValue(values[index])}
                </span>
              </div>
            );
          })}
        </div>

        {showCycleInfo && cycleEndDate && (
          <div className={styles.cycleSection}>
            <div className={styles.progressBar}>
              <div
                className={`${styles.progressFill} ${
                  isUrgent ? styles['progressFill--urgent'] : ''
                }`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className={styles.cycleInfo}>
              <span>
                Ciclo encerra em {formatCycleDate(cycleEndDate)} às 00:00
              </span>
              <span>{daysRemaining} dias restantes</span>
            </div>
          </div>
        )}
      </div>

      <div className={styles.expandSection}>
        <button
          type="button"
          className={styles.expandButton}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          <span>Como funciona</span>
          <IoChevronDown
            size={20}
            className={`${styles.chevron} ${
              expanded ? styles['chevron--expanded'] : ''
            }`}
          />
        </button>

        <div
          className={`${styles.expandContent} ${
            expanded ? styles['expandContent--open'] : ''
          }`}
        >
          <p className={styles.expandText}>
            A cada compra você acumula desconto no próximo pacote. Quanto antes
            começar, mais você economiza ao longo do ciclo.
          </p>
        </div>
      </div>
    </div>
  );
};
